import type { AppData, Settings } from "./types";

const DB_NAME = "finanzas-rafael";
const DB_VERSION = 2;

export const STORE_NAMES = [
  "products",
  "lots",
  "purchases",
  "sales",
  "expenses",
  "returns",
  "documents",
  "adjustments",
  "meta",
] as const;

export type StoreName = (typeof STORE_NAMES)[number];

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion ?? 0;
      STORE_NAMES.forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: "id" });
        }
      });
      // Limpieza única de los datos de ejemplo que se cargaban automáticamente
      // en versiones anteriores. Se conservan los ajustes del usuario ("meta").
      if (oldVersion > 0 && oldVersion < 2) {
        const upgradeTx = request.transaction;
        if (upgradeTx) {
          STORE_NAMES.filter((name) => name !== "meta").forEach((name) => {
            upgradeTx.objectStore(name).clear();
          });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

export async function readStore<T>(name: StoreName): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, "readonly");
    const store = tx.objectStore(name);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

export async function writeRecord<T extends { id: string }>(
  name: StoreName,
  record: T,
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, "readwrite");
    tx.objectStore(name).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function writeMany<T extends { id: string }>(
  name: StoreName,
  records: T[],
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, "readwrite");
    const store = tx.objectStore(name);
    records.forEach((r) => store.put(r));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeRecord(name: StoreName, id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, "readwrite");
    tx.objectStore(name).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearStores(names: StoreName[]): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(names, "readwrite");
    names.forEach((n) => tx.objectStore(n).clear());
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

const META_KEY = "settings";

export async function readSettings(): Promise<Settings | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("meta", "readonly");
    const request = tx.objectStore("meta").get(META_KEY);
    request.onsuccess = () => resolve((request.result as { value: Settings } | undefined)?.value ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function writeSettings(settings: Settings): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("meta", "readwrite");
    tx.objectStore("meta").put({ id: META_KEY, value: settings });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Lee todos los datos persistidos en una sola estructura. */
export async function readAll(): Promise<Partial<AppData>> {
  const [products, lots, purchases, sales, expenses, returns, documents, adjustments] =
    await Promise.all([
      readStore<AppData["products"][number]>("products"),
      readStore<AppData["lots"][number]>("lots"),
      readStore<AppData["purchases"][number]>("purchases"),
      readStore<AppData["sales"][number]>("sales"),
      readStore<AppData["expenses"][number]>("expenses"),
      readStore<AppData["returns"][number]>("returns"),
      readStore<AppData["documents"][number]>("documents"),
      readStore<AppData["adjustments"][number]>("adjustments"),
    ]);
  return { products, lots, purchases, sales, expenses, returns, documents, adjustments };
}